import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Calendar, Home, RefreshCw, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const LEAVE_TYPES = ['casual_leave', 'sick_leave', 'annual_leave', 'unpaid_leave'];

function fmtMonth(d) { return d.toISOString().slice(0, 7); }

export default function WFHVerificationPage() {
  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const [flagDate, setFlagDate] = useState(new Date(Date.now() - 86400000).toISOString().split('T')[0]);
  const [actionItem, setActionItem] = useState(null);
  const [decision, setDecision] = useState('present');
  const [leaveType, setLeaveType] = useState('casual_leave');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/hr/attendance/wfh-unverified?month=${month}`);
      setItems(res.data?.items || []);
      setCount(res.data?.count || 0);
    } catch {
      setItems([]); setCount(0);
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { loadData(); }, [loadData]);

  const triggerFlag = async () => {
    setFlagging(true);
    try {
      const res = await api.post(`/hr/attendance/flag-wfh-unverified?date=${flagDate}`);
      const r = res.data || {};
      if (r.skipped) {
        toast.info(`Skipped: ${r.skipped}`);
      } else {
        toast.success(
          `Flagged ${r.flagged} employees · ${r.skipped_with_timesheet} had timesheet · ` +
          `${r.skipped_with_attendance} had attendance · ${r.skipped_on_leave} on leave`
        );
      }
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not flag');
    } finally { setFlagging(false); }
  };

  const openAction = (item, dec) => {
    setActionItem(item);
    setDecision(dec);
    setLeaveType('casual_leave');
    setComment('');
  };

  const submitDecision = async () => {
    setSubmitting(true);
    try {
      const payload = { decision, comment: comment.trim() };
      if (decision === 'on_leave') payload.leave_type = leaveType;
      await api.post(`/hr/attendance/${actionItem.id}/wfh-decision`, payload);
      toast.success(`Marked as ${decision.replace('_', ' ')}`);
      setActionItem(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setSubmitting(false); }
  };

  const grouped = useMemo(() => {
    const byDate = {};
    items.forEach(it => {
      (byDate[it.date] = byDate[it.date] || []).push(it);
    });
    return Object.entries(byDate).sort(([a], [b]) => b.localeCompare(a));
  }, [items]);

  const monthDate = new Date(month + '-01');
  const prevMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() - 1); setMonth(fmtMonth(d)); };
  const nextMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() + 1); setMonth(fmtMonth(d)); };
  const monthLabel = monthDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="wfh-verification-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Home className="h-6 w-6 text-amber-500" /> WFH Verification
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Remote employees who didn&apos;t submit a timesheet are flagged here. Decide present, absent, or on leave.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="wfh-month-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-32 text-center">{monthLabel}</span>
          <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="wfh-month-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> Run WFH Flag Job
          </CardTitle>
          <CardDescription>
            Manually flag remote employees who skipped a timesheet on a given date. Idempotent — safe to re-run.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            value={flagDate}
            onChange={(e) => setFlagDate(e.target.value)}
            className="w-[160px] bg-background"
            data-testid="wfh-flag-date"
          />
          <Button onClick={triggerFlag} disabled={flagging} data-testid="wfh-flag-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${flagging ? 'animate-spin' : ''}`} />
            {flagging ? 'Flagging…' : 'Flag Missing Timesheets'}
          </Button>
          <p className="text-xs text-muted-foreground ml-2">
            Defaults to yesterday. Skips weekends and holidays automatically.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Pending WFH Decisions ({count})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : grouped.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No pending WFH decisions for {monthLabel}.</p>
          ) : (
            <div className="space-y-4">
              {grouped.map(([date, rows]) => (
                <div key={date} className="space-y-2">
                  <div className="flex items-center justify-between border-b pb-1">
                    <h3 className="text-sm font-semibold">{date}</h3>
                    <Badge variant="outline">{rows.length} pending</Badge>
                  </div>
                  <div className="space-y-1.5">
                    {rows.map(r => (
                      <div key={r.id} className="border rounded-lg p-3 hover:bg-muted/30" data-testid={`wfh-row-${r.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="font-medium text-sm">{r.employee_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {r.department || '—'} · {r.work_location || 'remote'}
                              {r.pending_timesheet && (
                                <span className="ml-2 text-blue-600">
                                  · Has {r.pending_timesheet.status} timesheet ({r.pending_timesheet.total_hours}h)
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10"
                              onClick={() => openAction(r, 'present')}
                              data-testid={`wfh-mark-present-${r.id}`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Present
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-500/30 hover:bg-blue-500/10"
                              onClick={() => openAction(r, 'on_leave')}
                              data-testid={`wfh-mark-leave-${r.id}`}
                            >
                              On Leave
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-500/30 hover:bg-red-500/10"
                              onClick={() => openAction(r, 'absent')}
                              data-testid={`wfh-mark-absent-${r.id}`}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" /> Absent
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!actionItem} onOpenChange={(o) => !o && setActionItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Mark {actionItem?.employee_name} ({actionItem?.date}) as{' '}
              {decision.replace('_', ' ')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {decision === 'on_leave' && (
              <div>
                <label className="text-sm font-medium">Leave Type</label>
                <Select value={leaveType} onValueChange={setLeaveType}>
                  <SelectTrigger className="mt-1" data-testid="wfh-leave-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAVE_TYPES.map(t => (
                      <SelectItem key={t} value={t}>
                        {t.replace('_', ' ').toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional comment for the employee"
              className="min-h-[60px]"
              data-testid="wfh-action-comment"
            />
            {decision === 'absent' && (
              <p className="text-xs text-amber-600 flex items-start gap-1">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Marking absent will count as LOP in the next payroll run.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionItem(null)}>Cancel</Button>
            <Button
              onClick={submitDecision}
              disabled={submitting}
              variant={decision === 'absent' ? 'destructive' : 'default'}
              data-testid="wfh-action-submit"
            >
              {submitting ? 'Saving…' : `Mark ${decision.replace('_', ' ')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
